/**
 * StoreDetailsModal — view full store details + action buttons (approve/reject/modify/stop/archive).
 * Extracted from ManagePlatform.js
 */
import {
  Store, User, Mail, Phone, FileImage, FileText, Calendar,
  Edit3, XCircle, CheckCircle, Loader2, Ban, Archive as ArchiveIcon,
  Trash2, AlertTriangle,
} from 'lucide-react';
import { useState } from 'react';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';

export default function StoreDetailsModal({
  open,
  onOpenChange,
  selectedStore,
  STORE_TYPES,
  BUSINESS_TYPES,
  isPdfDocument,
  isImageDocument,
  openDocument,
  formatDate,
  setShowModificationModal,
  setShowRejectModal,
  approveStore,
  processingStore,
  // Stop + Archive flow
  onStop,
  onArchive,
  // iter-233 (Feb 2026) — Cascade delete flow (proxy to Business backend)
  onCascadeDelete,
  language = 'ar',
}) {
  const isAr = language === 'ar';
  const [confirmDelete, setConfirmDelete] = useState(false);
  const storeStatus = selectedStore?.status;
  const isPending = storeStatus === 'pending' || storeStatus === 'needs_edit'
    || storeStatus === 'resubmitted' || storeStatus === 'edited' || storeStatus === 'pending_review';
  const isActive = storeStatus === 'active' || storeStatus === 'approved';
  const isStopped = storeStatus === 'stopped' || storeStatus === 'rejected';
  const renderDocument = (url, label, alt) => {
    if (!url) return null;
    return (
      <div className="bg-[#1a1a2e] rounded-xl p-3 border border-[#2a2a4e]">
        <p className="text-xs text-[#606060] mb-2">{label}</p>
        {isPdfDocument(url) ? (
          <button
            onClick={() => openDocument(url, alt)}
            className="flex items-center gap-2 text-blue-400 hover:text-blue-300 transition-colors p-3 bg-[#0a0a0a] rounded-lg border border-[#2a2a4e] hover:border-blue-500 w-full"
            data-testid={`open-doc-${alt}`}
          >
            <FileText className="h-8 w-8" />
            <span>فتح ملف PDF</span>
          </button>
        ) : isImageDocument(url) ? (
          <button
            onClick={() => openDocument(url, alt)}
            className="block w-full text-start"
            data-testid={`open-img-${alt}`}
          >
            <img
              src={url}
              alt={label}
              className="max-w-full h-32 object-cover rounded-lg border border-[#2a2a4e] hover:border-[#c9a227] transition-colors cursor-pointer"
            />
          </button>
        ) : (
          <button
            onClick={() => openDocument(url, alt)}
            className="flex items-center gap-2 text-blue-400 hover:text-blue-300 transition-colors p-3 bg-[#0a0a0a] rounded-lg border border-[#2a2a4e] hover:border-blue-500 w-full"
            data-testid={`open-other-${alt}`}
          >
            <FileText className="h-8 w-8" />
            <span>فتح المستند</span>
          </button>
        )}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#1a1a2e] border-[#2a2a4e] text-white max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="store-details-modal"
      >
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Store className="h-5 w-5 text-orange-500" />
            تفاصيل طلب المتجر
          </DialogTitle>
        </DialogHeader>

        {selectedStore && (
          <div className="space-y-6 py-4">
            {/* Store Name + Store UID */}
            <div className="bg-[#0a0a0a] rounded-xl p-4 border border-[#2a2a4e]">
              <h3 className="text-lg font-bold text-white mb-1">{selectedStore.name || 'بدون اسم'}</h3>
              {selectedStore.nameEn && <p className="text-[#808080]">{selectedStore.nameEn}</p>}
              {/* iter-233 — Show store_uid (CGBECxxxxx…) prominently, per user request. */}
              {(selectedStore.store_uid || selectedStore.id) && (
                <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px]" data-testid="store-uid-display">
                  {selectedStore.store_uid && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-[#c9a227]/10 border border-[#c9a227]/30 text-[#c9a227] font-mono" dir="ltr">
                      {selectedStore.store_uid}
                    </span>
                  )}
                  {selectedStore.id && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-white/5 border border-white/10 text-white/50 font-mono" dir="ltr" title={selectedStore.id}>
                      ID: {String(selectedStore.id).slice(0, 8)}…
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Info Grid */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-[#0a0a0a] rounded-xl p-4 border border-[#2a2a4e]">
                <p className="text-xs text-[#606060] mb-1">النشاط</p>
                <p className="text-white font-medium">
                  {STORE_TYPES[selectedStore.type] || selectedStore.type || 'غير محدد'}
                </p>
              </div>
              <div className="bg-[#0a0a0a] rounded-xl p-4 border border-[#2a2a4e]">
                <p className="text-xs text-[#606060] mb-1">نوع الشركة</p>
                <p className="text-white font-medium">
                  {BUSINESS_TYPES[selectedStore.business_type] || selectedStore.business_type || 'غير محدد'}
                </p>
              </div>
            </div>

            {/* Description */}
            {selectedStore.description && (
              <div className="bg-[#0a0a0a] rounded-xl p-4 border border-[#2a2a4e]">
                <h4 className="text-sm font-semibold text-[#c9a227] mb-3">الوصف</h4>
                <p className="text-white">{selectedStore.description}</p>
              </div>
            )}

            {/* Logo */}
            {selectedStore.logo && (
              <div className="bg-[#0a0a0a] rounded-xl p-4 border border-[#2a2a4e]">
                <h4 className="text-sm font-semibold text-[#c9a227] mb-3">شعار المتجر</h4>
                <img
                  src={selectedStore.logo}
                  alt="شعار المتجر"
                  className="max-w-[200px] h-auto rounded-lg border border-[#2a2a4e]"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </div>
            )}

            {/* Owner Info */}
            <div className="bg-[#0a0a0a] rounded-xl p-4 border border-[#2a2a4e]">
              <h4 className="text-sm font-semibold text-[#c9a227] mb-3 flex items-center gap-2">
                <User className="h-4 w-4" />
                معلومات المالك
              </h4>
              <div className="space-y-2">
                <p className="text-white">{selectedStore.owner_name || 'غير محدد'}</p>
                {selectedStore.owner_email && (
                  <p className="text-[#808080] flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    {selectedStore.owner_email}
                  </p>
                )}
                {selectedStore.owner_phone && (
                  <p className="text-[#808080] flex items-center gap-2">
                    <Phone className="h-4 w-4" />
                    <span dir="ltr">{selectedStore.owner_phone}</span>
                  </p>
                )}
              </div>
            </div>

            {/* Documents */}
            {(selectedStore.license_document || selectedStore.authorization_document || selectedStore.signature_document) && (
              <div className="bg-[#0a0a0a] rounded-xl p-4 border border-[#2a2a4e]">
                <h4 className="text-sm font-semibold text-[#c9a227] mb-3 flex items-center gap-2">
                  <FileImage className="h-4 w-4" />
                  المستندات
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {renderDocument(selectedStore.license_document, 'الرخصة التجارية', 'license')}
                  {renderDocument(selectedStore.authorization_document, 'رسالة التفويض', 'authorization')}
                  {renderDocument(selectedStore.signature_document, 'اعتماد التوقيع', 'signature')}
                </div>
              </div>
            )}

            {/* Request Date */}
            <div className="flex items-center justify-between text-sm text-[#808080]">
              <span className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                تاريخ الطلب
              </span>
              <span>{formatDate(selectedStore.created_at)}</span>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="border-[#2a2a4e] text-white"
            data-testid="store-details-close-btn"
          >
            {isAr ? 'إغلاق' : 'Close'}
          </Button>
          {isPending && (
            <>
              <Button
                onClick={() => {
                  onOpenChange(false);
                  setShowModificationModal(true);
                }}
                variant="outline"
                className="text-blue-400 border-blue-500/30 hover:bg-blue-500/10"
                data-testid="store-details-modify-btn"
              >
                <Edit3 className="h-4 w-4 me-2" />
                {isAr ? 'طلب تعديل' : 'Request Edit'}
              </Button>
              <Button
                onClick={() => {
                  onOpenChange(false);
                  setShowRejectModal(true);
                }}
                variant="outline"
                className="text-red-400 border-red-500/30 hover:bg-red-500/10"
                data-testid="store-details-reject-btn"
              >
                <XCircle className="h-4 w-4 me-2" />
                {isAr ? 'رفض' : 'Reject'}
              </Button>
              <Button
                onClick={() => selectedStore && approveStore(selectedStore.id)}
                disabled={processingStore === selectedStore?.id}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="store-details-approve-btn"
              >
                {processingStore === selectedStore?.id ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4 me-2" />
                    {isAr ? 'موافقة' : 'Approve'}
                  </>
                )}
              </Button>
            </>
          )}
          {isActive && onStop && (
            <Button
              onClick={() => onStop(selectedStore)}
              disabled={processingStore === selectedStore?.id}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="store-details-stop-btn"
            >
              <Ban className="h-4 w-4 me-2" />
              {isAr ? 'إيقاف المتجر' : 'Stop Store'}
            </Button>
          )}
          {(isActive || isStopped) && onArchive && (
            <Button
              onClick={() => isStopped && onArchive(selectedStore.id)}
              disabled={!isStopped || processingStore === selectedStore?.id}
              title={
                !isStopped
                  ? (isAr ? 'يجب إيقاف المتجر أولاً قبل الأرشفة' : 'You must stop the store first')
                  : ''
              }
              variant="outline"
              className={`border-amber-500/30 ${
                isStopped
                  ? 'text-amber-400 hover:bg-amber-500/10'
                  : 'text-amber-400/40 opacity-50 cursor-not-allowed'
              }`}
              data-testid="store-details-archive-btn"
            >
              {processingStore === selectedStore?.id ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <ArchiveIcon className="h-4 w-4 me-2" />
                  {isAr ? 'أرشفة' : 'Archive'}
                </>
              )}
            </Button>
          )}

          {/* iter-233 — Cascade delete (proxy to Business backend). Available
              for any store status. Requires 2-step confirmation. */}
          {onCascadeDelete && (
            <Button
              onClick={() => setConfirmDelete(true)}
              disabled={processingStore === selectedStore?.id}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="store-details-cascade-delete-btn"
            >
              <Trash2 className="h-4 w-4 me-2" />
              {isAr ? 'حذف الطلب نهائياً' : 'Delete Permanently'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>

      {/* Confirmation sub-dialog — 2-step delete flow */}
      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent className="bg-[#0a0a0a] border-rose-500/40 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-rose-300 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              {isAr ? 'تأكيد الحذف النهائي' : 'Confirm Permanent Deletion'}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-3 text-sm">
            <p className="text-white/80">
              {isAr
                ? 'سيؤدي هذا الإجراء إلى حذف نهائي على الطرفين (الأدمن ومنصة التجار):'
                : 'This action will permanently delete on BOTH sides (admin + merchant platform):'}
            </p>
            <ul className="space-y-1.5 text-white/70 ps-5 list-disc marker:text-rose-400">
              <li>{isAr ? `المتجر: "${selectedStore?.name || '—'}"` : `Store: "${selectedStore?.name || '—'}"`}</li>
              <li>{isAr ? 'كل المنتجات/الإعلانات المرتبطة' : 'All linked items / listings'}</li>
              <li>{isAr ? 'كل الطلبات ذات المتجر الواحد' : 'All single-store orders'}</li>
              <li>{isAr ? 'كل الإشعارات والمعاملات والسجلات' : 'All notifications, transactions, activity logs'}</li>
            </ul>
            <div className="p-3 rounded-lg bg-rose-500/10 border border-rose-500/30 text-rose-200 text-xs">
              ⚠️ {isAr ? 'لا يمكن التراجع عن هذا الإجراء.' : 'This action cannot be undone.'}
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setConfirmDelete(false)}
              disabled={processingStore === selectedStore?.id}
              data-testid="cascade-delete-cancel"
              className="text-white/70 border-white/20"
            >
              {isAr ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button
              onClick={async () => {
                setConfirmDelete(false);
                await onCascadeDelete(selectedStore?.id);
              }}
              disabled={processingStore === selectedStore?.id}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="cascade-delete-confirm"
            >
              {processingStore === selectedStore?.id ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Trash2 className="w-4 h-4 me-2" />
                  {isAr ? 'نعم، احذف نهائياً' : 'Yes, delete permanently'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
